/**
 * PreviewPane — Agent 4 implementation.
 *
 * INTEGRATION CONTRACT (ScratchpadPage.tsx imports this component and will not change):
 *
 * Props:
 *   selectedFileId: string | null
 *     - The ID of the agent file currently selected in the tree.
 *     - null when nothing is selected.
 *
 * This component renders three stacked sections per editor spec §3.6:
 *   1. Validation (errors + warnings from validateFrontmatter)
 *   2. Test context (JSON textarea for rule-trigger evaluation)
 *   3. Composed prompt (read-only, copyable) — this is the full agent prompt
 *
 * Below the sections: RunWithClaudeButton. There is no separate user message —
 * the composed agent prompt is the entire instruction set.
 * History button in the pane toolbar opens HistoryDrawer.
 */

import { useState, useMemo, useCallback } from "react";
import { Copy, Check, History } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { useAgentFiles } from "@/hooks/useAgentFiles";
import { validateFrontmatter, validateBody } from "@/lib/agent-skills/validation";
import { composePrompt } from "@/lib/agent-skills/compose";
import type { SectionStatus } from "@/lib/agent-skills/compose";
import type { EvalResult } from "@/lib/agent-skills/triggers";
import { RunWithClaudeButton } from "@/components/scratchpad/RunWithClaudeButton";
import { HistoryDrawer } from "@/components/scratchpad/HistoryDrawer";
import type { AgentFile } from "@/types/agent-skills";

export function PreviewPane({ selectedFileId }: { selectedFileId: string | null }) {
  const { rows, isLoading } = useAgentFiles();

  if (!selectedFileId) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
        Select a file to preview it.
      </div>
    );
  }

  if (isLoading) {
    return <PreviewSkeleton />;
  }

  const selectedFile = rows.find((r) => r.id === selectedFileId);
  if (!selectedFile) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
        File not found.
      </div>
    );
  }

  return <PreviewContent selectedFile={selectedFile} allFiles={rows} />;
}

// ── Skeleton ─────────────────────────────────────────────────────────────────

function PreviewSkeleton() {
  return (
    <div className="p-4 space-y-6">
      <Skeleton className="h-5 w-24" />
      <Skeleton className="h-20 w-full" />
      <Skeleton className="h-5 w-28" />
      <Skeleton className="h-16 w-full" />
      <Skeleton className="h-5 w-32" />
      <Skeleton className="h-40 w-full" />
      <Skeleton className="h-9 w-full" />
    </div>
  );
}

// ── Main content (when file is loaded) ───────────────────────────────────────

interface PreviewContentProps {
  selectedFile: AgentFile;
  allFiles: AgentFile[];
}

function PreviewContent({ selectedFile, allFiles }: PreviewContentProps) {
  const [testContextRaw, setTestContextRaw] = useState("");
  const [copied, setCopied] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  // Parse test context JSON
  const testContext = useMemo<Record<string, unknown> | null>(() => {
    if (!testContextRaw.trim()) return null;
    try {
      const parsed = JSON.parse(testContextRaw) as unknown;
      if (typeof parsed === "object" && parsed !== null && !Array.isArray(parsed)) {
        return parsed as Record<string, unknown>;
      }
      return null;
    } catch {
      return null;
    }
  }, [testContextRaw]);

  const testContextParseError =
    testContextRaw.trim() !== "" && testContext === null
      ? "Invalid JSON — check your test context."
      : null;

  // Collect siblings for uniqueness validation
  const siblings = useMemo(() => {
    return allFiles.filter(
      (f) =>
        f.parent_id === selectedFile.parent_id && f.id !== selectedFile.id && f.deleted_at === null
    );
  }, [allFiles, selectedFile]);

  // Validation
  const frontmatterValidation = useMemo(
    () => validateFrontmatter(selectedFile.frontmatter, selectedFile.kind, siblings),
    [selectedFile.frontmatter, selectedFile.kind, siblings]
  );
  const bodyWarnings = useMemo(() => validateBody(selectedFile.body), [selectedFile.body]);
  const validation = useMemo(
    () => ({
      errors: frontmatterValidation.errors,
      warnings: [...frontmatterValidation.warnings, ...bodyWarnings],
    }),
    [frontmatterValidation, bodyWarnings]
  );

  // Descendants in tree order
  const descendants = useMemo(
    () => getDescendants(selectedFile.id, allFiles),
    [selectedFile.id, allFiles]
  );

  // Composed prompt
  const { prompt: composedPrompt, sectionStatuses } = useMemo(
    () => composePrompt({ agent: selectedFile, descendants, testContext }),
    [selectedFile, descendants, testContext]
  );

  function handleCopyPrompt() {
    void navigator.clipboard.writeText(composedPrompt).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }

  const isDirty = false; // PreviewPane doesn't own dirty state; HistoryDrawer gets it from AgentFileEditor via prop

  return (
    <div className="glass-panel h-full flex flex-col overflow-y-auto p-4 space-y-6">
      {/* Toolbar with History button */}
      <div className="flex items-center justify-between shrink-0">
        <p className="text-sm font-medium text-muted-foreground">Preview</p>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setHistoryOpen(true)}
          className="gap-1.5"
        >
          <History className="w-3.5 h-3.5" />
          History
        </Button>
      </div>

      {/* 1. Validation */}
      <ValidationSection validation={validation} />

      <Separator />

      {/* 2. Test context (JSON) */}
      <div className="space-y-2">
        <label className="text-xs text-muted-foreground font-medium">Test context (JSON)</label>
        <Textarea
          value={testContextRaw}
          onChange={(e) => setTestContextRaw(e.target.value)}
          placeholder='{"vendor_name": "PG&E"}'
          className="min-h-[100px] font-mono text-xs"
          spellCheck={false}
        />
        {testContextParseError && (
          <p className="text-xs text-destructive">{testContextParseError}</p>
        )}
      </div>

      <Separator />

      {/* 3. Composed prompt */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <label className="text-xs text-muted-foreground font-medium">
            Composed prompt (system)
          </label>
          <Button variant="ghost" size="icon" className="w-7 h-7" onClick={handleCopyPrompt}>
            {copied ? (
              <Check className="w-3.5 h-3.5 text-success" />
            ) : (
              <Copy className="w-3.5 h-3.5" />
            )}
          </Button>
        </div>

        <ComposedPromptView prompt={composedPrompt} sectionStatuses={sectionStatuses} />
      </div>

      <Separator />

      {/* Run */}
      <RunWithClaudeButton
        systemPrompt={composedPrompt}
        validation={validation}
        selectedFileId={selectedFile.id}
      />

      {/* History drawer */}
      <HistoryDrawer
        open={historyOpen}
        onOpenChange={setHistoryOpen}
        fileId={selectedFile.id}
        currentVersion={selectedFile.current_version}
        isDirty={isDirty}
        currentFrontmatter={selectedFile.frontmatter}
        currentBody={selectedFile.body}
      />
    </div>
  );
}

// ── Validation section ────────────────────────────────────────────────────────

function ValidationSection({
  validation,
}: {
  validation: {
    errors: Array<{ code: string; message: string }>;
    warnings: Array<{ code: string; message: string }>;
  };
}) {
  const hasErrors = validation.errors.length > 0;
  const hasWarnings = validation.warnings.length > 0;

  return (
    <div className="space-y-2">
      <label className="text-xs text-muted-foreground font-medium">Validation</label>

      {!hasErrors && !hasWarnings && (
        <p className="text-xs text-success flex items-center gap-1.5">
          <Check className="w-3.5 h-3.5" />
          All checks pass
        </p>
      )}

      {hasErrors && (
        <ul className="space-y-1">
          {validation.errors.map((issue) => (
            <li key={issue.code} className="flex items-start gap-2 text-xs">
              <Badge variant="destructive" className="shrink-0 text-xs py-0">
                Error
              </Badge>
              <span>{issue.message}</span>
            </li>
          ))}
        </ul>
      )}

      {hasWarnings && (
        <ul className="space-y-1">
          {validation.warnings.map((issue) => (
            <li key={issue.code} className="flex items-start gap-2 text-xs">
              <Badge
                variant="outline"
                className="shrink-0 text-xs py-0 border-warning text-warning"
              >
                Warn
              </Badge>
              <span>{issue.message}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ── Composed prompt view ──────────────────────────────────────────────────────

function ruleBadgeText(result: EvalResult): string {
  switch (result) {
    case "match":
      return "✓ rule matched";
    case "no-match":
      return "✗ rule did not match";
    case "no-context":
      return "· no JSON context";
  }
}

function ComposedPromptView({
  prompt,
  sectionStatuses,
}: {
  prompt: string;
  sectionStatuses: SectionStatus[];
}) {
  // Render the prompt lines and annotate section headers with badges
  const lines = prompt.split("\n");

  // Build a map from header line text → SectionStatus
  const statusByHeader = new Map<string, SectionStatus>();
  for (const s of sectionStatuses) {
    if (s.kind === "skill") {
      statusByHeader.set(`## Skill: ${s.name}`, s);
    } else if (s.kind === "subskill") {
      statusByHeader.set(`## Sub-skill: ${s.name}`, s);
    }
  }

  return (
    <div className="bg-muted/40 rounded-md border border-border max-h-[300px] overflow-auto">
      <pre className="p-3 font-mono text-xs whitespace-pre-wrap">
        {lines.map((line, i) => {
          const status = statusByHeader.get(line);
          if (status?.triggerResult) {
            return (
              <span key={i}>
                {line}{" "}
                <span
                  className={
                    status.triggerResult === "match"
                      ? "text-success"
                      : status.triggerResult === "no-match"
                        ? "text-destructive"
                        : "text-muted-foreground"
                  }
                >
                  [{ruleBadgeText(status.triggerResult)}]
                </span>
                {"\n"}
              </span>
            );
          }
          return (
            <span key={i}>
              {line}
              {"\n"}
            </span>
          );
        })}
      </pre>
    </div>
  );
}

// ── Helper: get descendants in tree order ────────────────────────────────────

function getDescendants(rootId: string, allFiles: AgentFile[]): AgentFile[] {
  const result: AgentFile[] = [];
  const queue: string[] = [rootId];

  while (queue.length > 0) {
    const parentId = queue.shift()!;
    const children = allFiles.filter((f) => f.parent_id === parentId && f.deleted_at === null);
    for (const child of children) {
      result.push(child);
      queue.push(child.id);
    }
  }

  return result;
}
